import { ReactElement } from 'react';
import { KineticDetail } from '../breakdownMath';
import { formatNumber } from '../breakdownFormat';
import FormulaRow from './FormulaRow';

interface Props {
  detail: KineticDetail;
  duration: number;
  combinedMass: number;
  wheelCircumference: number;
  pipelinePower: number;
}

export default function KineticSection({
  detail,
  duration,
  combinedMass,
  wheelCircumference,
  pipelinePower,
}: Props): ReactElement {
  const { revSpdStart, revSpdEnd, vStart, vEnd, keStart, keEnd, work, power } = detail;
  const pipelineWork = Number.isFinite(pipelinePower) ? pipelinePower * duration : NaN;
  const delta = Number.isFinite(pipelineWork) ? work - pipelineWork : NaN;
  const deltaPct = Number.isFinite(delta) && pipelineWork !== 0
    ? (delta / pipelineWork) * 100
    : NaN;

  return (
    <div className="cr-py-2">
      <p className="cr-text-sm cr-text-gray-500 cr-mb-4">
        Change in kinetic energy across the traverse.
      </p>

      <div className="cr-mb-3 cr-text-sm cr-font-medium cr-text-gray-700">Start</div>
      <FormulaRow
        className="cr-mb-2"
        inputs={[
          { header: 'Wheel speed', value: `${formatNumber(revSpdStart, 3)} rev/s` },
          { header: '× Circumference', value: `${formatNumber(wheelCircumference, 4)} m` },
        ]}
        result={{ header: 'Linear speed', value: `${formatNumber(vStart, 2)} m/s` }}
      />
      <FormulaRow
        className="cr-mb-5"
        inputs={[
          { header: '½', value: '0.5' },
          { header: '× Mass', value: `${formatNumber(combinedMass, 1)} kg` },
          { header: '× v²', value: `${formatNumber(vStart * vStart, 3)} m²/s²` },
        ]}
        result={{ header: 'KE start', value: `${formatNumber(keStart, 0)} J` }}
      />

      <div className="cr-mb-3 cr-text-sm cr-font-medium cr-text-gray-700">End</div>
      <FormulaRow
        className="cr-mb-2"
        inputs={[
          { header: 'Wheel speed', value: `${formatNumber(revSpdEnd, 3)} rev/s` },
          { header: '× Circumference', value: `${formatNumber(wheelCircumference, 4)} m` },
        ]}
        result={{ header: 'Linear speed', value: `${formatNumber(vEnd, 2)} m/s` }}
      />
      <FormulaRow
        className="cr-mb-5"
        inputs={[
          { header: '½', value: '0.5' },
          { header: '× Mass', value: `${formatNumber(combinedMass, 1)} kg` },
          { header: '× v²', value: `${formatNumber(vEnd * vEnd, 3)} m²/s²` },
        ]}
        result={{ header: 'KE end', value: `${formatNumber(keEnd, 0)} J` }}
      />

      <div className="cr-pt-4 cr-border-t cr-border-gray-200">
        <FormulaRow
          inputs={[
            { header: 'KE start', value: `${formatNumber(keStart, 0)} J` },
            { header: '− KE end', value: `${formatNumber(keEnd, 0)} J` },
            { header: '÷ Time', value: `${formatNumber(duration, 2)} s` },
          ]}
          result={{ header: 'Kinetic power', value: `${formatNumber(power, 1)} W` }}
        />
        <div className="cr-text-xs cr-text-gray-500 cr-mt-2 cr-italic">
          Kinetic work: {formatNumber(work, 0)} J. Pipeline convention:{' '}
          <code>kinetic = KE_start − KE_end</code> (negative when accelerating, positive when
          decelerating, so all retarding forces are negative in the energy balance).
        </div>
      </div>

      {Number.isFinite(pipelinePower) && (
        <div className="cr-mt-3 cr-pt-3 cr-border-t cr-border-gray-200 cr-text-sm">
          <div className="cr-text-gray-700 cr-font-medium cr-mb-1">Pipeline comparison</div>
          <div className="cr-grid cr-grid-cols-3 cr-gap-3 cr-font-mono cr-text-gray-700">
            <div>
              <div className="cr-text-xs cr-text-gray-500">Pipeline (Kinetic_PG3 × ΔT)</div>
              <div>{formatNumber(pipelineWork, 0)} J</div>
            </div>
            <div>
              <div className="cr-text-xs cr-text-gray-500">Client</div>
              <div>{formatNumber(work, 0)} J</div>
            </div>
            <div>
              <div className="cr-text-xs cr-text-gray-500">Δ (client − pipeline)</div>
              <div className={delta && Math.abs(deltaPct) > 1 ? 'cr-text-amber-700' : ''}>
                {formatNumber(delta, 0)} J
                {Number.isFinite(deltaPct) && (
                  <span className="cr-ml-2 cr-text-xs">
                    ({deltaPct >= 0 ? '+' : ''}{formatNumber(deltaPct, 2)}%)
                  </span>
                )}
              </div>
            </div>
          </div>
          <p className="cr-text-xs cr-text-gray-500 cr-mt-2 cr-italic">
            Pipeline computes <code>Kinetic_PG3 = Kappa_XG2 × m × C²</code>, where{' '}
            <code>Kappa_XG2 = ½(ω_end² − ω_start²) / ΔT</code> is independent of mass and
            circumference. Client uses{' '}
            <code>½ × m × (v_end² − v_start²)</code> with v_start/v_end from{' '}
            <code>WhlLspdStart_vG3C</code> / <code>WhlLspdEnd_vG3C</code>. If wheel circumference
            has been edited since processing, the precomputed v&apos;s reflect the old C while the
            pipeline reflects the new one — giving a small (C_old/C_new)² mismatch.
          </p>
        </div>
      )}
    </div>
  );
}
